import Element from '../../../Prototype/UI/Element';
import GameObject, { GameObjectEvent } from '../../../GameObject/GameObject';
import AnimationComponent from '../../../Component/Animation';
import ButtonComponent from '../../../Component/UI/ButtonComponent';
import { applyDefaultButtonAnimation } from '../../../System/UI/UIHelper';
import { isShopSupported } from '../../../Utility/Platform';
import Game from '../../../Game';
import { UIActions } from '../../../State/UI/UIActions';
import { FBTrackingEvent } from '../../../Utility/Tracking/FBTrackingEvent';
import StateComponent from '../../../Component/State';
import { IGameState } from '../../../State/Game/IGameState';
import { GameScene } from '../../../State/Game/GameScene';
import { PlayerTask } from '../../../State/Player/PlayerTasks';
import UITextComponent from '../../../Component/UI/Text';
import { StartAnimation, StartAnimationClipAsync } from '../../../System/Animation/AnimationHelper';
import { LevelStatus } from '../../../State/Level/LevelStatus';
import { TextAlignment } from '../../../Prototype/UI/TextAlignment';
import { PopupID } from '../../../State/UI/PopupID';
import { addDragonbonesResources } from '../../../Utility/Resource/ResourceHelper';
import { DRAGONBONES_PATH } from '../../../Constants/Dragonbones';
import Armature from '../../../Prototype/Armature';
import Point from '../../../Utility/Point';
import Renderable from '../../../Component/Renderable';
import TransformComponent from '../../../Component/Transform';
import { DragonbonesDefault } from '../../../System/Animation/Clip';
import { Logger, LogStyles, LogOrigin } from '../../../Utility/Logger/LoggerSuite';
import { SHOP_UNLOCK_LEVEL } from 'Constants/Shop';

/**
 * @fixme For some reason TS throws a hissy if you try to assign an imported external `let` so this is to fix that by assigning it in a function.
 */
export function hackForceCurrencyAnimation(): void {
    _hackForceAnim = true;
}
let _hackForceAnim: boolean = false;

export default class CoinBar extends Element {
    public static _name: string;
    public static BACKGROUND_NAME: string = 'coin_bar_bg';
    public static BUTTON_NAME: string = 'more_coins_button';
    public static TEXT_NAME: string = 'coin_text';
    public static COIN_NAME: string = 'coin_bar_coin';

    private currentValue: number = 0;
    private idleArmature: GameObject | null = null;
    private dbAnimInitialized: boolean = false;

    private _animation: DragonbonesDefault;
    private _timeoutID: number = undefined;

    public create(params: any): void {
        this.currentValue = Game.store.getState().playerState.inventory.coins;
        CoinBar._name = params.name || 'coin_bar';
        const state: IGameState = Game.store.getState();
        const isLevel: boolean = state.activeScene.result === GameScene.LEVEL;
        super.create({
            name: CoinBar._name,
            layout: {
                anchors: [0.5, 0.5, 0.5, 0.5],
                pivot: [0.5, 0.5],
                offsets: [0, 0, 0, 0],
                scale: [1, 1],
                ...params.layout,
            },
            ...params,
            children: [
                {
                    name: CoinBar.BACKGROUND_NAME,
                    layout: {
                        anchors: [0.5, 0.5, 0.5, 0.5],
                        pivot: [0.5, isLevel ? 0 : 0.5],
                        offsets: [0, 0, 0, 0],
                        scale: [1, 1],
                    },
                    components: [
                        {
                            type: 'image',
                            data: {
                                cacheKey: isLevel
                                    ? 'level_coin_bar_bg'
                                    : 'coin_bar_bg',
                            },
                        },
                        {
                            type: 'input',
                            data: {
                                buttonMode: true,
                                enabled: true,
                            },
                        },
                    ],
                    children: [
                        {
                            name: CoinBar.BUTTON_NAME,
                            layout: {
                                anchors: [0.85, 0.5, 0.85, 0.5],
                                pivot: [0.5, 0.5],
                                offsets: [0, 0, 0, 0],
                            },
                            components: [
                                {
                                    type: 'image',
                                    data: {
                                        cacheKey: isLevel
                                            ? 'plus_ingame'
                                            : 'more_coins_plus_icon',
                                    },
                                },
                                {
                                    type: 'visibility',
                                    data: {
                                        hidden:
                                            Game.player.getHighestLevelCompleted(
                                                state
                                            ) < SHOP_UNLOCK_LEVEL,
                                    },
                                },
                            ],
                        },
                        {
                            name: CoinBar.TEXT_NAME,
                            layout: {
                                anchors: [0.5, 0.5, 0.5, 0.5],
                                pivot: [0.5, 0.55],
                                offsets: [0, 0, 0, 0],
                            },
                            components: [
                                {
                                    type: 'text',
                                    data: {
                                        text: String(this.currentValue),
                                        style: {
                                            font: 'FrancoisOne',
                                            fill: '#892e2e',
                                            fontSize: '30px',
                                            align: TextAlignment.CENTER,
                                        },
                                        bounds: [isLevel ? 90 : 125, 80],
                                        align: TextAlignment.CENTER,
                                    },
                                },
                            ],
                        },
                        {
                            name: CoinBar.COIN_NAME,
                            layout: {
                                anchors: [0.1, 0.5, 0.1, 0.5],
                                pivot: [0.5, 0.5],
                                offsets: [0, 0, 0, 0],
                            },
                            components: [
                                {
                                    type: 'image',
                                    data: {
                                        cacheKey: isLevel
                                            ? 'level_coin_medium'
                                            : 'coin_large',
                                    },
                                },
                            ],
                        },
                    ],
                },
            ],
        });
        this.addComponent(AnimationComponent);

        CoinBar.loadDBAssets().then(this.onDBAssetsLoaded).catch((err) =>
            Logger.log(err, LogOrigin.COINBAR, LogStyles.ERROR)
        );

        const button: GameObject = this.button;
        const background: GameObject = this.background;
        background.addComponent(ButtonComponent, {
            toggleable: true,
        });
        background.on(GameObjectEvent.SELECTED, this.onSelect.bind(this));
        applyDefaultButtonAnimation(button);
        this.addComponent(StateComponent, {
            handleStateChanged: this.handleStateChanged,
        });
    }

    private readonly onDBAssetsLoaded = () => {
        if (this.destroyed) return;
        const coinImage = this.coinImage;
        this.idleArmature = GameObject.Instantiate(Armature, {
            name: 'coin_reward',
            dragonbonesName: 'coin_reward',
            armatureName: 'Armature',
            animationName: 'animtion0',
            alpha: 1,
            playOnStart: false,
            playTimes: 1,
            position: new Point(
                coinImage.container.x,
                coinImage.container.y
            ),
        });

        this.idleArmature.getComponent(TransformComponent).width = this.coinImage.container.width / 54;
        this.idleArmature.getComponent(TransformComponent).height = this.coinImage.container.height / 54;
    };

    private playDBAnim(): Promise<void> {
        return new Promise((resolve: Function /*, reject: Function*/): void => {
            if(!this.idleArmature) {
                return resolve();
            }

            if(this._animation) {
                this._animation.destroy();
                this._animation = null;
            }

            if(this._timeoutID) {
                clearTimeout(this._timeoutID);
                this._timeoutID = undefined;
            }

            this._timeoutID = setTimeout(() => {
                this._timeoutID = undefined;
                if(
                    this.idleArmature &&
                    !this.dbAnimInitialized &&
                    this.coinImage &&
                    this.background
                ) {
                    this.dbAnimInitialized = true;
                    this.coinImage.getComponent(Renderable).visible = false;
                    this.background.addChild(this.idleArmature);
                } else {
                    return;
                }
                this._animation = new DragonbonesDefault(this.idleArmature, {
                    animationName: 'animtion0',
                    playTimes: 1,
                });
                StartAnimationClipAsync(this._animation)
                    .then(() => this._animation = null, resolve())
                    .catch((err) => {
                        Logger.log(err, LogOrigin.COINBAR, LogStyles.ERROR);
                        resolve();
                    });
            }, 200) as unknown as number;
        });
    }

    private static async loadDBAssets(): Promise<void> {
        addDragonbonesResources(
            'coin_reward',
            `${DRAGONBONES_PATH}/Coinbar/`
        );
        await Game.resource.load();
        Logger.log('Loaded DB anim', LogOrigin.COINBAR);
    }

    private onSelect(): void {
        // play an anim when clicked for funsies
        this.playDBAnim();

        if (Game.player.getHighestLevelCompleted() < SHOP_UNLOCK_LEVEL) {
            return;
        }

        if (isShopSupported()) {
            UIActions.showPopup(PopupID.SHOP_MENU, {});

            try {
                Game.sdk.gameTracking(FBTrackingEvent.OpenShop_CoinBar);
            } catch (e) {
                console.error('Failed to track', e);
            }
        } else {
            !isShopSupported()
                ? UIActions.showPopup(PopupID.NO_COINS_MENU, {})
                : UIActions.showPopup(PopupID.SHOP_MENU, {});
        }
    }

    public get text(): GameObject {
        try {
            return this.findChildByName(CoinBar.TEXT_NAME);
        } catch (e) {
            console.warn(`${this.name}'s 'text' could not be found`);
        }
    }
    public get button(): GameObject {
        try {
            return this.findChildByName(CoinBar.BUTTON_NAME);
        } catch (e) {
            console.warn(`${this.name}'s 'button' could not be found`);
        }
    }
    public get background(): GameObject {
        try {
            return this.findChildByName(CoinBar.BACKGROUND_NAME);
        } catch (e) {
            console.warn(`${this.name}'s 'background' could not be found`);
        }
    }
    public get coinImage(): GameObject {
        try {
            return this.findChildByName(CoinBar.COIN_NAME);
        } catch (e) {
            console.warn(`${this.name}'s 'coinImage' could not be found`);
        }
    }
    public readonly handleStateChanged = (state: IGameState): void => {
        // don't immediately update coins if coming from level complete
        if (
            state.levelState.status === LevelStatus.COMPLETE &&
            Game.store.getPreviousState().levelState.status ===
                LevelStatus.COMPLETE
        )
            return;

        let newValue: number = state.playerState.inventory.coins;
        const coinTask = state.playerState.tasks.find(
            task => task.task === PlayerTask.COIN
        );

        if (coinTask && state.activeScene.result !== GameScene.LEVEL && !_hackForceAnim) {
            newValue = coinTask.previousCoins;
            this.currentValue = state.playerState.inventory.coins;
        } else if (this.currentValue !== newValue) {
            if (state.activeScene.result === GameScene.LEVEL || _hackForceAnim) {
                StartAnimation(this.text, 'Counter', {
                    startValue: this.currentValue,
                    finalValue: state.playerState.inventory.coins,
                    onUpdateOverride: (_value: number) => {
                        this.setCoinsText(_value.toFixed(0));
                    },
                    delay:
                        this.currentValue < state.playerState.inventory.coins
                            ? 1000
                            : 20,
                    duration: 450,
                });
            }
            newValue = undefined;
            this.currentValue = state.playerState.inventory.coins;
        }

        if(_hackForceAnim) {
            _hackForceAnim = false;
            this.playDBAnim();
        }

        if(newValue) {
            this.setCoinsText(newValue);
        }
    }
    public setCoinsText(_value: number | string) {
        const target = this.text.getComponentsInChildren(
            UITextComponent
        )[0];

        if(target.text === String(_value)) {
            return;
        }

        target.text = typeof _value === 'number' ? _value.toString() : _value;

        if(_hackForceAnim) {
            _hackForceAnim = false;
            this.playDBAnim();
        }
    }
}
